"use strict";
/**
 * Rules for an element's Configuration Data rows (`ec__custom_data`) — the author's own key/value
 * pairs on a digital element (SSP-4218).
 *
 * Unlike template-level custom data, repeated keys are permitted by design: one question can carry
 * several sponsor identifiers. So the only rules here are about rows that carry no information.
 *
 * They live in `libs.consent.common` rather than beside either authoring panel because both panels
 * write the same field and the persistence path normalizes it once for all of them.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.findHalfPairCustomDataRow = exports.isHalfPairCustomDataRow = exports.customDataRowGap = exports.normalizeCustomDataRows = exports.isBlankCustomDataRow = void 0;
/**
 * Coerces before trimming because stored rows are cast rather than parsed on the way in, so a
 * legacy `{key: 'visit_code'}` written before `value` was always present reaches here with the
 * field absent. That is literally "a key and no value" — the shape these rules exist to report —
 * so throwing on it would take out the surfaces that report it: the config form runs this while
 * loading an element, and the choice panel runs it on every blur.
 */
const trimRow = ({ key, value }) => ({
    key: (key !== null && key !== void 0 ? key : '').trim(),
    value: (value !== null && value !== void 0 ? value : '').trim(),
});
/**
 * A row the author added and then left completely alone. Adding a row is one click, so an abandoned
 * blank row is ordinary authoring debris rather than a mistake worth reporting.
 */
const isBlankCustomDataRow = (row) => {
    const trimmed = trimRow(row);
    return trimmed.key === '' && trimmed.value === '';
};
exports.isBlankCustomDataRow = isBlankCustomDataRow;
/**
 * Trims every row and drops the blank ones, so a template never persists rows that mean nothing.
 *
 * Deliberately not applied on keystroke: the author needs the blank row they just added to stay on
 * screen while they type into it. It runs at the save boundary instead.
 */
const normalizeCustomDataRows = (rows = []) => rows.map(trimRow).filter((row) => !(0, exports.isBlankCustomDataRow)(row));
exports.normalizeCustomDataRows = normalizeCustomDataRows;
/**
 * The half a row is missing.
 *
 * A half-pair row is a half-finished entry rather than debris — dropping it would silently discard
 * something the author started, so it is reported instead. Both halves are reported, and for the
 * same reason: every consumer reads these rows *by key* (the MOM projector, both bundler copies,
 * the GSK tag queries), so a row carrying only a value is unaddressable — it is arguably worse off
 * than one carrying only a key, which at least has a handle (SSP-4543).
 *
 * Returns which half is missing rather than a boolean because the surfaces mark the field that is
 * actually empty and name the row by the half it does have; a caller that only needs the question
 * answered uses `isHalfPairCustomDataRow`.
 */
const customDataRowGap = (row) => {
    const trimmed = trimRow(row);
    if (trimmed.key !== '' && trimmed.value === '') {
        return 'value';
    }
    if (trimmed.key === '' && trimmed.value !== '') {
        return 'key';
    }
    return null;
};
exports.customDataRowGap = customDataRowGap;
/**
 * A row carrying one half of its pair and not the other.
 *
 * Exported as a predicate as well as the finder below because the choice panel asks about one row
 * at a time: it marks the row focus has just left, not the first half-pair row on the card.
 */
const isHalfPairCustomDataRow = (row) => (0, exports.customDataRowGap)(row) !== null;
exports.isHalfPairCustomDataRow = isHalfPairCustomDataRow;
/**
 * Position of the first row carrying one half of its pair and not the other, or `-1`.
 *
 * The index is into the rows as given, so it still addresses the row the author is looking at.
 */
const findHalfPairCustomDataRow = (rows = []) => rows.findIndex(exports.isHalfPairCustomDataRow);
exports.findHalfPairCustomDataRow = findHalfPairCustomDataRow;
//# sourceMappingURL=element_custom_data.js.map